import { axiosBaseQuery, createAxiosInstance } from '@client';
import { getEnvironmentVariables } from '@common';
import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { HlAiBankAccountDetails, HlAiDaHelperV2Response } from '@state';
import { S3ObjectResponse } from '@module';
import { HlAiDocumentRecogniserResponse } from '@state/types/HlAiDocumentRecogniserResponse';
import { DAExpenseCategories } from './models';
import {
  HlAiNegotiatorBenchmarkResposne,
  HlAiNegotiatorEmailResposne,
  HlAiNegotiatorPdaDocumentResponse,
  HlAiNegotiatorRangesDocumentResponse
} from '@state/types/HlAiNegotiatorResponse';
import { HlAiNegotiatorClarificationsInput } from '@state/types/HlAiNegotiatorRequest';

const { HL_AI_API_URL } = getEnvironmentVariables();

const axiosHarborlabAiInstance = createAxiosInstance({
  baseURLParam: `${HL_AI_API_URL}`
});

export type ExtractBankAccountDetailsTrigger = ReturnType<
  typeof useExtractBankAccountDetailsMutation
>[0];

export type ExtractDocumentDetailsSyncTrigger = ReturnType<
  typeof useExtractDocumentTextSyncMutation
>[0];

export type LocateHusbandryExpensesInAttachmentTrigger = ReturnType<
  typeof useLocateHusbandryExpensesInAttachmentMutation
>[0];

export type LocatePortExpensesInAttachmentTrigger = ReturnType<
  typeof useLocatePortExpensesInAttachmentMutation
>[0];

export type PollForResultsMutationTrigger = ReturnType<
  typeof usePollForResultsMutation
>[0];

export type VerifyDocumentTypeTrigger = ReturnType<
  typeof useVerifyDocumentTypeMutation
>[0];

export type PollForDocumentTypeVerificationTrigger = ReturnType<
  typeof usePollForDocumentTypeVerificationMutation
>[0];

export type AiNegotiatorAsyncTrigger = ReturnType<
  typeof useAiNegotiatorMutation
>[0];

export type AiNegotiatorEmailAgentAsyncTrigger = ReturnType<
  typeof useAiNegotiatorEmailAgentMutation
>[0];

export const harborlabAiApi = createApi({
  baseQuery: axiosBaseQuery({
    baseUrl: '',
    customAxiosInstance: axiosHarborlabAiInstance
  }),
  reducerPath: 'harborlab-ai',
  endpoints: (build) => ({
    aiNegotiator: build.mutation<void, { proforma_id: number; uuid: string }>({
      query: ({ proforma_id, uuid }) => ({
        url: '/ai-negotiator',
        method: 'POST',
        data: { da_id: proforma_id, uuid }
      })
    }),
    aiNegotiatorEmailAgent: build.mutation<
      void,
      {
        proforma_id: number;
        uuid: string;
        s3FileKey: string;
        clarifications?: HlAiNegotiatorClarificationsInput[];
      }
    >({
      query: ({ proforma_id, uuid, s3FileKey, clarifications }) => ({
        url: '/ai-negotiator-email-agent',
        method: 'POST',
        data: {
          da_id: proforma_id,
          uuid,
          comparison_agent_s3_key: s3FileKey,
          clarifications
        }
      })
    }),
    extractBankAccountDetails: build.mutation<
      HlAiBankAccountDetails,
      S3ObjectResponse
    >({
      query: (payload) => ({
        url: '/bank-account-details',
        method: 'POST',
        data: payload
      })
    }),
    locatePortExpensesInAttachment: build.mutation<
      void,
      {
        s3ObjectDetails: S3ObjectResponse;
        portId: string;
        daExpenses: DAExpenseCategories;
      }
    >({
      query: ({ s3ObjectDetails, portId, daExpenses }) => ({
        url: '/da-helper-port',
        method: 'POST',
        data: {
          s3Bucket: s3ObjectDetails.s3Bucket,
          s3FileKey: s3ObjectDetails.s3FileKey,
          portExpensesS3FileKey: `port/${portId}/portExpensesOntology.json`,
          daExpenses: daExpenses
        }
      })
    }),
    locateHusbandryExpensesInAttachment: build.mutation<
      void,
      { s3ObjectDetails: S3ObjectResponse; daExpenses: DAExpenseCategories }
    >({
      query: ({ s3ObjectDetails, daExpenses }) => ({
        url: '/da-helper-husbandry',
        method: 'POST',
        data: {
          s3Bucket: s3ObjectDetails.s3Bucket,
          s3FileKey: s3ObjectDetails.s3FileKey,
          daExpenses: daExpenses
        }
      })
    }),
    pollForResults: build.mutation<
      | HlAiDaHelperV2Response
      | HlAiBankAccountDetails
      | HlAiNegotiatorPdaDocumentResponse
      | HlAiNegotiatorRangesDocumentResponse
      | HlAiNegotiatorBenchmarkResposne
      | HlAiNegotiatorEmailResposne,
      { s3ObjectDetails: S3ObjectResponse; fileName: string }
    >({
      query: ({ s3ObjectDetails, fileName }) => ({
        url: '/results-poller',
        method: 'POST',
        data: {
          s3Bucket: s3ObjectDetails.s3Bucket,
          s3FileKey: s3ObjectDetails.s3FileKey,
          fileName
        }
      })
    }),
    verifyDocumentType: build.mutation<
      void,
      { s3ObjectDetails: S3ObjectResponse; documentType: string; uuid: string }
    >({
      query: ({ s3ObjectDetails, documentType, uuid }) => ({
        url: '/document-recogniser',
        method: 'POST',
        data: {
          s3Bucket: s3ObjectDetails.s3Bucket,
          s3FileKey: s3ObjectDetails.s3FileKey,
          documentType,
          uuid
        }
      })
    }),
    pollForDocumentTypeVerification: build.mutation<
      HlAiDocumentRecogniserResponse,
      { s3ObjectDetails: S3ObjectResponse; uuid: string }
    >({
      query: ({ s3ObjectDetails, uuid }) => ({
        url: '/document-recogniser-results-poller',
        method: 'POST',
        data: {
          s3Bucket: s3ObjectDetails.s3Bucket,
          s3FileKey: s3ObjectDetails.s3FileKey,
          uuid
        }
      })
    }),
    extractDocumentText: build.mutation<
      void,
      { s3ObjectDetails: S3ObjectResponse }
    >({
      query: ({ s3ObjectDetails }) => ({
        url: '/pdf-text-extractor',
        method: 'POST',
        data: {
          s3Bucket: s3ObjectDetails.s3Bucket,
          s3Key: s3ObjectDetails.s3FileKey
        }
      })
    }),
    extractDocumentTextSync: build.mutation<
      void,
      { s3ObjectDetails: S3ObjectResponse }
    >({
      query: ({ s3ObjectDetails }) => ({
        url: '/pdf-text-extractor-sync',
        method: 'POST',
        data: {
          s3Bucket: s3ObjectDetails.s3Bucket,
          s3Key: s3ObjectDetails.s3FileKey
        }
      })
    }),
    extractSofEvents: build.mutation<
      void,
      { s3ObjectDetails: S3ObjectResponse; uuid: string }
    >({
      query: ({ s3ObjectDetails, uuid }) => ({
        url: '/sof-event-extractor',
        method: 'POST',
        data: {
          s3Bucket: s3ObjectDetails.s3Bucket,
          s3FileKey: s3ObjectDetails.s3FileKey,
          uuid
        }
      })
    })
  })
});

export type StateWithHarborlabAiApi = {
  [harborlabAiApi.reducerPath]: ReturnType<typeof harborlabAiApi.reducer>;
};

export const {
  useAiNegotiatorMutation,
  useAiNegotiatorEmailAgentMutation,
  useExtractBankAccountDetailsMutation,
  useExtractDocumentTextMutation,
  useExtractDocumentTextSyncMutation,
  useExtractSofEventsMutation,
  useLocateHusbandryExpensesInAttachmentMutation,
  useLocatePortExpensesInAttachmentMutation,
  usePollForDocumentTypeVerificationMutation,
  usePollForResultsMutation,
  useVerifyDocumentTypeMutation
} = harborlabAiApi;
